/**
 * Dedicated Telegram DataSource panel — the canonical place to manage Telegram
 * accounts, watched groups, and per-group historical backfill for the CRM
 * Customer Memory pipeline. Everything is encapsulated in crm-service via the
 * MTProto user-session Watcher; the panel talks only to `/v1/telegram-dedicated`.
 */
import { useState, useCallback, useEffect, useMemo, type ReactNode } from 'react'
import {
    Box,
    Stack,
    Sheet,
    Typography,
    Button,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Modal,
    ModalDialog,
    ModalClose,
    Divider,
    FormControl,
    FormLabel,
    Input,
    LinearProgress,
    Switch,
} from '@mui/joy'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import LogoutIcon from '@mui/icons-material/Logout'
import GroupIcon from '@mui/icons-material/Group'
import PersonIcon from '@mui/icons-material/Person'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import KeyIcon from '@mui/icons-material/Key'
import LockIcon from '@mui/icons-material/Lock'
import SyncIcon from '@mui/icons-material/Sync'
import TelegramIcon from '@mui/icons-material/Telegram'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ScienceIcon from '@mui/icons-material/Science'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ReplayIcon from '@mui/icons-material/Replay'
import MicIcon from '@mui/icons-material/Mic'
import { EtlPreviewModal } from '../shared/EtlPreviewModal'

import {
    type OracleTgAccount,
    type OracleTgGroup,
    type ResolvedPeer,
    type OracleTgBackfillProgress,
    oracleTgListAccounts,
    oracleTgSendCode,
    oracleTgSignIn,
    oracleTgLogout,
    oracleTgStartWatcher,
    oracleTgStopWatcher,
    oracleTgListGroupsForAccount,
    oracleTgListCustomerGroupsForAccount,
    oracleTgListDialogs,
    oracleTgJoinChat,
    oracleTgWatchGroup,
    oracleTgWatchCustomerGroup,
    oracleTgUnwatchGroup,
    oracleTgUnwatchCustomerGroup,
    oracleTgUpdateGroup,
    oracleTgUpdateCustomerGroup,
    oracleTgGetBackfillStatus,
    oracleTgStartBackfill,
    oracleTgCancelBackfill,
    type OracleTgMediaPrepareProgress,
    oracleTgGetMediaPrepareStatus,
    oracleTgStartMediaPrepare,
    oracleTgCancelMediaPrepare,
} from 'api/oracleTelegramApi'
import {
    type TelegramDedicatedStats,
    type TelegramDedicatedRecentMessage,
    type TelegramMediaStatus,
    type TelegramChannelHealth,
    type TelegramChannelStatus,
    type ListTelegramDedicatedMessagesParams,
    getTelegramDedicatedStats,
    getTelegramDedicatedChannels,
    listTelegramDedicatedMessages,
    deleteTelegramDedicatedMessage,
    requeueTelegramDedicatedMessage,
    previewTelegramDedicatedBatch,
    deleteTelegramDedicatedChannel,
} from 'api/oracleApi'
import { useUrlPathSegment } from '../../hooks/useUrlPathSegment'

export function TelegramDedicatedPanel({ onBack }: { onBack: () => void }): JSX.Element {
    const [selectedPeerId, setSelectedPeerId] = useUrlPathSegment(1)
    const [accounts, setAccounts] = useState<OracleTgAccount[]>([])
    const [channels, setChannels] = useState<TelegramChannelHealth[]>([])
    const [globalStats, setGlobalStats] = useState<TelegramDedicatedStats | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [accountsOpen, setAccountsOpen] = useState(false)
    const [etlPreviewOpen, setEtlPreviewOpen] = useState(false)
    const [manageFor, setManageFor] = useState<OracleTgAccount | null>(null)

    const reload = useCallback(async () => {
        setLoading(true)
        try {
            const [a, c, s] = await Promise.all([oracleTgListAccounts(), getTelegramDedicatedChannels(), getTelegramDedicatedStats().catch(() => null)])
            setAccounts(a)
            setChannels(c)
            setGlobalStats(s)
        } catch (err) {
            setError(`Failed to load: ${(err as Error)?.message || String(err)}`)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void reload()
    }, [reload])

    const handleStart = async (phone: string) => {
        try {
            await oracleTgStartWatcher(phone)
            await reload()
        } catch (err) {
            setError(`Start failed: ${(err as Error)?.message || String(err)}`)
        }
    }
    const handleStop = async (phone: string) => {
        try {
            await oracleTgStopWatcher(phone)
            await reload()
        } catch (err) {
            setError(`Stop failed: ${(err as Error)?.message || String(err)}`)
        }
    }
    const handleLogout = async (phone: string) => {
        if (!window.confirm(`Log out ${phone}? This deletes the session — you'll need to re-auth to use it again.`)) return
        try {
            await oracleTgLogout(phone)
            await reload()
        } catch (err) {
            setError(`Logout failed: ${(err as Error)?.message || String(err)}`)
        }
    }

    const selectedChannel = useMemo(() => channels.find((c) => c.peerId === selectedPeerId) ?? null, [channels, selectedPeerId])

    return (
        <Stack gap={2}>
            <Stack direction="row" alignItems="center" gap={1}>
                <IconButton size="sm" variant="plain" onClick={onBack}>
                    <ArrowBackIcon />
                </IconButton>
                <TelegramIcon sx={{ color: 'primary.500' }} />
                <Typography level="h4">Dedicated Telegram</Typography>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Refresh" size="sm">
                    <IconButton size="sm" variant="outlined" onClick={() => void reload()} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
                <Button size="sm" variant="outlined" startDecorator={<PersonIcon sx={{ fontSize: 14 }} />} onClick={() => setAccountsOpen(true)}>
                    Accounts {accounts.length > 0 ? `· ${accounts.length}` : ''}
                </Button>
                <PrepareMediaButton pendingCount={globalStats?.mediaAwaitingTranscription ?? 0} />
                <WatchGroupShortcut accounts={accounts} onOpenAccounts={() => setAccountsOpen(true)} onPickAccount={(a) => setManageFor(a)} />
            </Stack>

            {error && (
                <Alert
                    color="danger"
                    size="sm"
                    endDecorator={
                        <IconButton size="sm" variant="plain" onClick={() => setError('')}>
                            ×
                        </IconButton>
                    }
                >
                    {error}
                </Alert>
            )}

            {selectedChannel ? (
                <ChannelDetailView
                    channel={selectedChannel}
                    onBack={() => setSelectedPeerId(null)}
                    onRefresh={() => void reload()}
                    onOpenBackfill={(phone) => phone && setManageFor(accounts.find((a) => a.phone === phone) ?? null)}
                />
            ) : (
                <ChannelsListView
                    channels={channels}
                    globalStats={globalStats}
                    loading={loading}
                    onOpenChannel={(peerId) => setSelectedPeerId(peerId)}
                    onOpenEtlPreview={() => setEtlPreviewOpen(true)}
                />
            )}

            {accountsOpen && (
                <AccountsModal
                    accounts={accounts}
                    loading={loading}
                    onClose={() => setAccountsOpen(false)}
                    onAdded={() => void reload()}
                    onManage={(a) => {
                        setAccountsOpen(false)
                        setManageFor(a)
                    }}
                    onStart={handleStart}
                    onStop={handleStop}
                    onLogout={handleLogout}
                />
            )}

            <EtlPreviewModal open={etlPreviewOpen} onClose={() => setEtlPreviewOpen(false)} onRunPreview={previewTelegramDedicatedBatch} />

            {manageFor && (
                <ManageWatchedGroupsModal
                    account={manageFor}
                    onClose={() => {
                        setManageFor(null)
                        void reload()
                    }}
                />
            )}
        </Stack>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Channels list (default view)
// ─────────────────────────────────────────────────────────────────────────────

type ChannelFilter = 'all' | 'active' | 'stale' | 'error' | 'paused'
type ChannelSort = 'lag' | 'name' | 'lastMsg' | 'pending'

function ChannelsListView({
    channels,
    globalStats,
    loading,
    onOpenChannel,
    onOpenEtlPreview,
}: {
    channels: TelegramChannelHealth[]
    globalStats: TelegramDedicatedStats | null
    loading: boolean
    onOpenChannel: (peerId: string) => void
    onOpenEtlPreview: () => void
}): JSX.Element {
    const [filter, setFilter] = useState<ChannelFilter>('all')
    const [sort, setSort] = useState<ChannelSort>('lag')
    const [search, setSearch] = useState('')

    const aggregate = useMemo(() => {
        const a = { pending: 0, processed: 0, unattributed: 0, failed: 0, mediaPending: 0, mediaProcessing: 0, mediaFailed: 0 }
        for (const c of channels) {
            a.pending += c.counts.pending
            a.processed += c.counts.processed
            a.unattributed += c.counts.unattributed
            a.failed += c.counts.failed
            a.mediaPending += c.media.pending
            a.mediaProcessing += c.media.processing
            a.mediaFailed += c.media.failed
        }
        return a
    }, [channels])

    const counts = useMemo(() => {
        const c = { all: channels.length, active: 0, stale: 0, error: 0, paused: 0 }
        for (const ch of channels) c[ch.status]++
        return c
    }, [channels])

    const visible = useMemo(() => {
        let list = filter === 'all' ? channels.slice() : channels.filter((c) => c.status === filter)
        if (search.trim()) {
            const q = search.trim().toLowerCase()
            list = list.filter(
                (c) => (c.name ?? '').toLowerCase().includes(q) || (c.brokerCompanyName ?? '').toLowerCase().includes(q) || c.peerId.toLowerCase().includes(q)
            )
        }
        list.sort((a, b) => {
            if (sort === 'name') return (a.name ?? a.peerId).localeCompare(b.name ?? b.peerId)
            if (sort === 'lastMsg') return (Date.parse(b.lastIngestedAt ?? '0') || 0) - (Date.parse(a.lastIngestedAt ?? '0') || 0)
            if (sort === 'pending') return b.counts.pending - a.counts.pending
            // lag desc default
            return (b.lagSec ?? -1) - (a.lagSec ?? -1)
        })
        return list
    }, [channels, filter, search, sort])

    return (
        <Stack gap={1.5}>
            <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 1.5 }}>
                <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                    <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap" flex={1}>
                        <Chip size="sm" variant="soft" color="warning">
                            {aggregate.pending} pending
                        </Chip>
                        <Chip size="sm" variant="soft" color="success">
                            {aggregate.processed} processed
                        </Chip>
                        <Chip size="sm" variant="soft" color="neutral">
                            {aggregate.unattributed} unattributed
                        </Chip>
                        {aggregate.failed > 0 && (
                            <Chip size="sm" variant="soft" color="danger">
                                {aggregate.failed} failed
                            </Chip>
                        )}
                        {aggregate.mediaProcessing + aggregate.mediaPending > 0 && (
                            <Chip size="sm" variant="soft" color="primary" startDecorator={<MicIcon sx={{ fontSize: 11 }} />}>
                                {aggregate.mediaProcessing + aggregate.mediaPending} media queued
                            </Chip>
                        )}
                        {globalStats?.lastIngestedAt && (
                            <Typography level="body-xs" color="neutral">
                                · last ingest {relTime(globalStats.lastIngestedAt)}
                            </Typography>
                        )}
                    </Stack>
                    <Button size="sm" variant="outlined" startDecorator={<ScienceIcon sx={{ fontSize: 14 }} />} onClick={onOpenEtlPreview}>
                        Dry Run ETL
                    </Button>
                </Stack>
                {globalStats?.weeklyVolume && globalStats.weeklyVolume.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>
                            Last 7 days
                        </Typography>
                        <WeeklyBars data={globalStats.weeklyVolume} />
                    </Box>
                )}
            </Sheet>

            <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 1.5 }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                    <Typography level="title-sm">Channels ({counts.all})</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Input
                        size="sm"
                        startDecorator={<SearchIcon sx={{ fontSize: 14 }} />}
                        placeholder="Search…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ width: 200 }}
                    />
                    <Typography level="body-xs" color="neutral">
                        Sort:
                    </Typography>
                    {(['lag', 'name', 'lastMsg', 'pending'] as const).map((s) => (
                        <Chip key={s} size="sm" variant={sort === s ? 'solid' : 'soft'} color="neutral" onClick={() => setSort(s)} sx={{ cursor: 'pointer' }}>
                            {s === 'lag' ? 'Lag' : s === 'name' ? 'Name' : s === 'lastMsg' ? 'Last msg' : 'Pending'}
                        </Chip>
                    ))}
                </Stack>
                <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mb: 1.5 }}>
                    {(['all', 'active', 'stale', 'error', 'paused'] as const).map((f) => (
                        <Chip
                            key={f}
                            size="sm"
                            variant={filter === f ? 'solid' : 'soft'}
                            color={f === 'all' ? 'neutral' : channelStatusColor(f as TelegramChannelStatus)}
                            onClick={() => setFilter(f)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {f} · {counts[f]}
                        </Chip>
                    ))}
                </Stack>

                {loading && channels.length === 0 ? (
                    <Stack alignItems="center" py={3}>
                        <CircularProgress size="sm" />
                    </Stack>
                ) : visible.length === 0 ? (
                    <Sheet variant="soft" sx={{ p: 3, borderRadius: 'sm', textAlign: 'center' }}>
                        <Typography level="body-sm" color="neutral">
                            No channels matching this filter. Add a Telegram account and watch a group to start ingesting.
                        </Typography>
                    </Sheet>
                ) : (
                    <Stack gap={0.75}>
                        {visible.map((c) => (
                            <ChannelRow key={c.peerId} channel={c} onOpen={() => onOpenChannel(c.peerId)} />
                        ))}
                    </Stack>
                )}
            </Sheet>
        </Stack>
    )
}

function ChannelRow({ channel, onOpen }: { channel: TelegramChannelHealth; onOpen: () => void }): JSX.Element {
    const title = channel.name || channel.peerId
    return (
        <Sheet
            variant="outlined"
            onClick={onOpen}
            sx={{
                borderRadius: 'sm',
                p: 1.25,
                cursor: 'pointer',
                transition: 'background-color 120ms',
                '&:hover': { bgcolor: 'background.level1' },
            }}
        >
            <Stack direction="row" alignItems="center" gap={1.5}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                        <ChannelStatusChip status={channel.status} />
                        <Typography level="title-sm" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {title}
                        </Typography>
                        {channel.brokerCompanyName && (
                            <Chip size="sm" variant="soft" color="warning">
                                {channel.brokerCompanyName}
                            </Chip>
                        )}
                    </Stack>
                    <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mb: 0.5 }}>
                        <FactItem label="Last msg" value={channel.lastIngestedAt ? relTime(channel.lastIngestedAt) : '—'} />
                        <FactItem label="Pending" value={String(channel.counts.pending)} />
                        {channel.counts.pending > 0 && channel.oldestPendingAt && <FactItem label="Oldest pend." value={relTime(channel.oldestPendingAt)} />}
                        {channel.media.pending + channel.media.processing > 0 && (
                            <FactItem
                                label="Media queue"
                                value={`${channel.media.pending + channel.media.processing}${channel.media.failed > 0 ? ` (${channel.media.failed} failed)` : ''}`}
                            />
                        )}
                        {channel.counts.failed > 0 && <FactItem label="Failed" value={String(channel.counts.failed)} danger />}
                    </Stack>
                    <CursorStrip channel={channel} />
                </Box>
                <Typography level="h4" sx={{ color: 'text.tertiary', lineHeight: 1 }}>
                    ›
                </Typography>
            </Stack>
        </Sheet>
    )
}

function FactItem({ label, value, danger }: { label: string; value: string; danger?: boolean }): JSX.Element {
    return (
        <Stack direction="row" gap={0.5} alignItems="baseline">
            <Typography level="body-xs" color="neutral" sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
            </Typography>
            <Typography level="body-xs" color={danger ? 'danger' : undefined} sx={{ fontWeight: 'md' }}>
                {value}
            </Typography>
        </Stack>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Cursor strip — oldest pending · last processed · last ingested + lag
// ─────────────────────────────────────────────────────────────────────────────

export function CursorStrip({ channel }: { channel: TelegramChannelHealth }): JSX.Element {
    const { oldestPendingAt, lastProcessedAt, lastIngestedAt, lagSec } = channel
    if (!lastIngestedAt && !lastProcessedAt) {
        return (
            <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                no activity yet
            </Typography>
        )
    }
    // Build the strip: left dot = oldestPending or processed; triangle = processed; right dot = lastIngested
    const showOldest = !!oldestPendingAt && oldestPendingAt !== lastProcessedAt
    return (
        <Stack direction="row" alignItems="center" gap={1}>
            <Box sx={{ position: 'relative', flex: 1, height: 12, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', height: '1px', bgcolor: 'divider' }} />
                {showOldest && (
                    <Tooltip title={`Oldest pending: ${new Date(oldestPendingAt as string).toLocaleString()}`} size="sm">
                        <Box sx={{ position: 'absolute', left: '4%', width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.500' }} />
                    </Tooltip>
                )}
                {lastProcessedAt && (
                    <Tooltip title={`Last processed: ${new Date(lastProcessedAt).toLocaleString()}`} size="sm">
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '5px solid transparent',
                                borderRight: '5px solid transparent',
                                borderBottom: '8px solid var(--joy-palette-success-500)',
                            }}
                        />
                    </Tooltip>
                )}
                {lastIngestedAt && (
                    <Tooltip title={`Last ingested: ${new Date(lastIngestedAt).toLocaleString()}`} size="sm">
                        <Box sx={{ position: 'absolute', right: '4%', width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.500' }} />
                    </Tooltip>
                )}
            </Box>
            <Typography
                level="body-xs"
                sx={{ fontFamily: 'monospace', minWidth: 70, textAlign: 'right' }}
                color={lagSec && lagSec > 3600 ? 'danger' : 'neutral'}
            >
                {lagSec == null ? '—' : lagSec === 0 ? '✓ caught up' : `lag −${formatDuration(lagSec)}`}
            </Typography>
        </Stack>
    )
}

function formatDuration(sec: number): string {
    if (sec < 60) return `${sec}s`
    const m = Math.floor(sec / 60)
    if (m < 60) return `${m}m`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h${m % 60 ? ` ${m % 60}m` : ''}`
    const d = Math.floor(h / 24)
    return `${d}d${h % 24 ? ` ${h % 24}h` : ''}`
}

function ChannelStatusChip({ status }: { status: TelegramChannelStatus }): JSX.Element {
    return (
        <Chip size="sm" variant="soft" color={channelStatusColor(status)}>
            {status}
        </Chip>
    )
}

function channelStatusColor(status: TelegramChannelStatus): 'success' | 'warning' | 'danger' | 'neutral' {
    return status === 'active' ? 'success' : status === 'stale' ? 'warning' : status === 'error' ? 'danger' : 'neutral'
}

// ─────────────────────────────────────────────────────────────────────────────
// Channel detail (drill-in) — shared component + dedicated wrapper
// ─────────────────────────────────────────────────────────────────────────────

export function TelegramChannelDetailView({
    channel,
    onBack,
    onRefresh,
    listMessages,
    requeueMessage,
    deleteMessage,
    headerActions,
}: {
    channel: TelegramChannelHealth
    onBack: () => void
    onRefresh: () => void
    listMessages: (params: ListTelegramDedicatedMessagesParams) => Promise<{ items: TelegramDedicatedRecentMessage[]; nextCursor: string | null }>
    requeueMessage: (id: string) => Promise<void>
    deleteMessage: (id: string) => Promise<void>
    headerActions?: ReactNode
}): JSX.Element {
    const [items, setItems] = useState<TelegramDedicatedRecentMessage[]>([])
    const [nextCursor, setNextCursor] = useState<string | null>(null)
    const [cursorStack, setCursorStack] = useState<(string | null)[]>([null])
    const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'processed' | 'unattributed' | 'failed'>('all')
    const [mediaFilter, setMediaFilter] = useState<'all' | TelegramMediaStatus>('all')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [selected, setSelected] = useState<TelegramDedicatedRecentMessage | null>(null)

    const load = useCallback(
        async (curs: string | null) => {
            setLoading(true)
            setError('')
            try {
                const res = await listMessages({
                    chat_id: channel.chatId,
                    status: statusFilter === 'all' ? undefined : statusFilter,
                    media_status: mediaFilter === 'all' ? undefined : mediaFilter,
                    cursor: curs ?? undefined,
                    limit: 30,
                    direction: 'older',
                })
                setItems(res.items)
                setNextCursor(res.nextCursor)
            } catch (err) {
                setError((err as Error)?.message || String(err))
            } finally {
                setLoading(false)
            }
        },
        [channel.chatId, listMessages, statusFilter, mediaFilter]
    )

    useEffect(() => {
        setCursorStack([null])
        void load(null)
    }, [statusFilter, mediaFilter, load])

    const currentCursor = cursorStack[cursorStack.length - 1] ?? null
    const goOlder = () => {
        if (!nextCursor) return
        setCursorStack((s) => [...s, nextCursor])
        void load(nextCursor)
    }
    const goNewer = () => {
        if (cursorStack.length <= 1) return
        const popped = cursorStack.slice(0, -1)
        setCursorStack(popped)
        void load(popped[popped.length - 1] ?? null)
    }

    const handleRequeue = async (id: string) => {
        try {
            await requeueMessage(id)
            await load(currentCursor)
            onRefresh()
        } catch (err) {
            setError((err as Error)?.message || String(err))
        }
    }
    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this message permanently?')) return
        try {
            await deleteMessage(id)
            await load(currentCursor)
            onRefresh()
        } catch (err) {
            setError((err as Error)?.message || String(err))
        }
    }

    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 2 }}>
            <Stack gap={1.5}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <IconButton size="sm" variant="plain" onClick={onBack}>
                        <ArrowBackIcon />
                    </IconButton>
                    <TelegramIcon sx={{ fontSize: 16, color: 'primary.500' }} />
                    <Typography level="title-md" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {channel.name || channel.peerId}
                    </Typography>
                    {channel.brokerCompanyName && (
                        <Chip size="sm" variant="soft" color="warning">
                            {channel.brokerCompanyName}
                        </Chip>
                    )}
                    <ChannelStatusChip status={channel.status} />
                    {headerActions}
                    <IconButton size="sm" variant="outlined" onClick={() => void load(currentCursor)} disabled={loading}>
                        <RefreshIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Stack>

                <Sheet variant="soft" sx={{ borderRadius: 'sm', p: 1.25 }}>
                    <CursorStrip channel={channel} />
                    <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mt: 1 }}>
                        <FactItem label="Pending" value={String(channel.counts.pending)} />
                        <FactItem label="Processed" value={String(channel.counts.processed)} />
                        <FactItem label="Unattributed" value={String(channel.counts.unattributed)} />
                        <FactItem label="Failed" value={String(channel.counts.failed)} danger={channel.counts.failed > 0} />
                        {(channel.media?.pending ?? 0) + (channel.media?.processing ?? 0) > 0 && (
                            <FactItem label="Media queue" value={`${(channel.media?.pending ?? 0) + (channel.media?.processing ?? 0)}`} />
                        )}
                        {(channel.media?.failed ?? 0) > 0 && <FactItem label="Media failed" value={String(channel.media?.failed)} danger />}
                        {channel.watchingPhones.length > 0 && <FactItem label="Watcher" value={channel.watchingPhones.join(', ')} />}
                    </Stack>
                </Sheet>

                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography level="body-xs" color="neutral">
                        Status:
                    </Typography>
                    {(['all', 'pending', 'processed', 'unattributed', 'failed'] as const).map((s) => (
                        <Chip
                            key={s}
                            size="sm"
                            variant={statusFilter === s ? 'solid' : 'soft'}
                            color={s === 'all' ? 'neutral' : statusColor(s)}
                            onClick={() => setStatusFilter(s)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {s}
                        </Chip>
                    ))}
                    <Box sx={{ width: 12 }} />
                    <Typography level="body-xs" color="neutral">
                        Media:
                    </Typography>
                    {(['all', 'pending', 'processing', 'transcribed', 'failed', 'none'] as const).map((m) => (
                        <Chip
                            key={m}
                            size="sm"
                            variant={mediaFilter === m ? 'solid' : 'soft'}
                            color="primary"
                            onClick={() => setMediaFilter(m)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {m}
                        </Chip>
                    ))}
                </Stack>

                {error && (
                    <Alert color="danger" size="sm">
                        {error}
                    </Alert>
                )}
                {loading && <LinearProgress />}

                <Box sx={{ maxHeight: '55vh', minHeight: 220, overflow: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {items.length === 0 && !loading && (
                        <Sheet variant="soft" sx={{ p: 3, borderRadius: 'sm', textAlign: 'center' }}>
                            <Typography level="body-sm" color="neutral">
                                No messages in this channel matching these filters.
                            </Typography>
                        </Sheet>
                    )}
                    {items.map((m) => (
                        <Sheet key={m.id} variant="outlined" sx={{ borderRadius: 'sm', p: 1 }}>
                            <Stack direction="row" gap={1} alignItems="flex-start">
                                <Chip size="sm" variant="soft" color={statusColor(m.status)} sx={{ flexShrink: 0 }}>
                                    {m.status}
                                </Chip>
                                {m.media_type && (
                                    <Chip size="sm" variant="soft" color="primary" startDecorator={<MicIcon sx={{ fontSize: 11 }} />} sx={{ flexShrink: 0 }}>
                                        {m.media_type} · {m.media_status ?? '—'}
                                    </Chip>
                                )}
                                <Stack flex={1} minWidth={0} gap={0.25}>
                                    <Typography level="body-xs" color="neutral">
                                        <strong>{m.from_username || m.from_id}</strong> · {new Date(m.created_at).toLocaleString()}
                                    </Typography>
                                    <Typography
                                        level="body-sm"
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                        }}
                                    >
                                        {m.text || m.caption || m.transcribed_text || '(empty)'}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" gap={0.25} flexShrink={0}>
                                    <Tooltip title="View details" size="sm">
                                        <IconButton size="sm" variant="plain" onClick={() => setSelected(m)}>
                                            <VisibilityIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Requeue (mark pending)" size="sm">
                                        <IconButton size="sm" variant="plain" color="primary" onClick={() => void handleRequeue(m.id)}>
                                            <ReplayIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete" size="sm">
                                        <IconButton size="sm" variant="plain" color="danger" onClick={() => void handleDelete(m.id)}>
                                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </Sheet>
                    ))}
                </Box>

                <Stack direction="row" gap={1} justifyContent="center" alignItems="center" sx={{ pt: 1 }}>
                    <Button size="sm" variant="outlined" disabled={cursorStack.length <= 1 || loading} onClick={goNewer}>
                        Newer
                    </Button>
                    <Typography level="body-xs" color="neutral" sx={{ minWidth: 60, textAlign: 'center' }}>
                        Page {cursorStack.length}
                    </Typography>
                    <Button size="sm" variant="outlined" disabled={!nextCursor || loading} onClick={goOlder}>
                        Older
                    </Button>
                </Stack>

                {selected && <MessageDetailModal message={selected} onClose={() => setSelected(null)} />}
            </Stack>
        </Sheet>
    )
}

function ChannelDetailView({
    channel,
    onBack,
    onRefresh,
}: {
    channel: TelegramChannelHealth
    onBack: () => void
    onRefresh: () => void
    onOpenBackfill: (phone: string | null) => void
}): JSX.Element {
    const [backfillOpen, setBackfillOpen] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [deleteError, setDeleteError] = useState('')
    const [messageReloadKey, setMessageReloadKey] = useState(0)

    const handleDeleteChannel = async () => {
        const label = channel.name || channel.peerId
        const msg = `Delete channel "${label}"?\n\nThis permanently removes:\n· all messages for this channel\n· watch subscriptions\n· dedup state\n· the group row itself\n\nThis cannot be undone.`
        if (!window.confirm(msg)) return
        setDeleting(true)
        try {
            const result = await deleteTelegramDedicatedChannel(channel.peerId)
            window.alert(
                `Deleted ${result.messages} messages, ${result.watches} watches, ${result.processed} dedup records${result.groupRowDeleted ? ', and the group row' : ''}.`
            )
            onBack()
            onRefresh()
        } catch (err) {
            setDeleteError(`Delete failed: ${(err as Error)?.message || String(err)}`)
        } finally {
            setDeleting(false)
        }
    }

    const backfillPhone = channel.watchingPhones[0] ?? null
    const groupForBackfill = useMemo<OracleTgGroup>(
        () => ({
            peerId: channel.peerId,
            originalName: channel.name,
            brokerCompanyName: channel.brokerCompanyName,
            joinLink: null,
            status: 'active',
            createdAt: '',
            updatedAt: '',
        }),
        [channel]
    )

    const handleBackfillComplete = useCallback(() => {
        setBackfillOpen(false)
        setMessageReloadKey((k) => k + 1)
        onRefresh()
    }, [onRefresh])

    return (
        <Stack gap={1}>
            {deleteError && (
                <Alert
                    color="danger"
                    size="sm"
                    endDecorator={
                        <IconButton size="sm" variant="plain" onClick={() => setDeleteError('')}>
                            ×
                        </IconButton>
                    }
                >
                    {deleteError}
                </Alert>
            )}
            <TelegramChannelDetailView
                key={messageReloadKey}
                channel={channel}
                onBack={onBack}
                onRefresh={onRefresh}
                listMessages={listTelegramDedicatedMessages}
                requeueMessage={requeueTelegramDedicatedMessage}
                deleteMessage={deleteTelegramDedicatedMessage}
                headerActions={
                    <>
                        <Tooltip title={backfillPhone ? `Backfill via ${backfillPhone}` : 'No watching account'} size="sm">
                            <span>
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    startDecorator={<SyncIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => setBackfillOpen(true)}
                                    disabled={!backfillPhone}
                                >
                                    Backfill
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Delete channel and all its data from Firestore" size="sm">
                            <Button
                                size="sm"
                                variant="outlined"
                                color="danger"
                                startDecorator={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                                onClick={() => void handleDeleteChannel()}
                                loading={deleting}
                            >
                                Delete
                            </Button>
                        </Tooltip>
                    </>
                }
            />
            {backfillOpen && backfillPhone && (
                <BackfillDialog phone={backfillPhone} group={groupForBackfill} onClose={() => setBackfillOpen(false)} onComplete={handleBackfillComplete} />
            )}
        </Stack>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Accounts modal — wraps existing AccountsTable + SignInModal
// ─────────────────────────────────────────────────────────────────────────────

export function AccountsModal({
    accounts,
    loading,
    onClose,
    onAdded,
    onManage,
    onStart,
    onStop,
    onLogout,
}: {
    accounts: OracleTgAccount[]
    loading: boolean
    onClose: () => void
    onAdded: () => void
    onManage: (a: OracleTgAccount) => void
    onStart: (phone: string) => Promise<void>
    onStop: (phone: string) => Promise<void>
    onLogout: (phone: string) => Promise<void>
}): JSX.Element {
    const [signInOpen, setSignInOpen] = useState(false)
    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{ minWidth: 720, maxWidth: 920, maxHeight: '90vh', overflow: 'auto' }}>
                <ModalClose />
                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <PersonIcon />
                    <Typography level="h4">Accounts</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button size="sm" variant="solid" color="primary" startDecorator={<AddIcon />} onClick={() => setSignInOpen(true)}>
                        Add Account
                    </Button>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <AccountsTable accounts={accounts} loading={loading} onManage={onManage} onStart={onStart} onStop={onStop} onLogout={onLogout} />
                <SignInModal
                    open={signInOpen}
                    onClose={() => setSignInOpen(false)}
                    onSuccess={async () => {
                        setSignInOpen(false)
                        onAdded()
                    }}
                />
            </ModalDialog>
        </Modal>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Watch-group shortcut button
// ─────────────────────────────────────────────────────────────────────────────

function WatchGroupShortcut({
    accounts,
    onOpenAccounts,
    onPickAccount,
}: {
    accounts: OracleTgAccount[]
    onOpenAccounts: () => void
    onPickAccount: (a: OracleTgAccount) => void
}): JSX.Element {
    const [pickerOpen, setPickerOpen] = useState(false)
    const activeAccounts = useMemo(() => accounts.filter((a) => a.status !== 'logged_out'), [accounts])
    const handleClick = () => {
        if (activeAccounts.length === 0) return onOpenAccounts()
        if (activeAccounts.length === 1) return onPickAccount(activeAccounts[0])
        setPickerOpen(true)
    }
    return (
        <>
            <Button size="sm" variant="solid" color="primary" startDecorator={<AddIcon />} onClick={handleClick}>
                Watch group
            </Button>
            {pickerOpen && (
                <Modal open onClose={() => setPickerOpen(false)}>
                    <ModalDialog sx={{ minWidth: 360 }}>
                        <ModalClose />
                        <Typography level="h4">Pick an account</Typography>
                        <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                            Which Telegram account should watch the new group?
                        </Typography>
                        <Stack gap={0.5}>
                            {activeAccounts.map((a) => (
                                <Button
                                    key={a.phone}
                                    variant="outlined"
                                    onClick={() => {
                                        setPickerOpen(false)
                                        onPickAccount(a)
                                    }}
                                >
                                    {a.phone}
                                </Button>
                            ))}
                        </Stack>
                    </ModalDialog>
                </Modal>
            )}
        </>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Prepare-media button — transcribes any media still in 'pending'/'failed'
// ─────────────────────────────────────────────────────────────────────────────

function PrepareMediaButton({ pendingCount }: { pendingCount: number }): JSX.Element {
    const [open, setOpen] = useState(false)
    const [status, setStatus] = useState<OracleTgMediaPrepareProgress | null>(null)
    const [error, setError] = useState('')
    const [starting, setStarting] = useState(false)

    const refresh = useCallback(async () => {
        try {
            const s = await oracleTgGetMediaPrepareStatus()
            setStatus(s)
        } catch (err) {
            setError((err as Error)?.message || String(err))
        }
    }, [])

    useEffect(() => {
        if (!open) return
        void refresh()
        const id = window.setInterval(() => void refresh(), 1500)
        return () => window.clearInterval(id)
    }, [open, refresh])

    // Local 1s tick so the "elapsed" timer on the current step doesn't wait
    // for the next poll to advance.
    const [, setTick] = useState(0)
    useEffect(() => {
        if (!open) return
        const id = window.setInterval(() => setTick((t) => t + 1), 1000)
        return () => window.clearInterval(id)
    }, [open])

    const isRunning = status?.status === 'running'

    const start = async () => {
        setStarting(true)
        setError('')
        try {
            await oracleTgStartMediaPrepare()
            await refresh()
        } catch (err) {
            setError((err as Error)?.message || String(err))
        } finally {
            setStarting(false)
        }
    }

    const cancel = async () => {
        try {
            await oracleTgCancelMediaPrepare()
            await refresh()
        } catch (err) {
            setError((err as Error)?.message || String(err))
        }
    }

    const progressPct = status && status.total > 0 ? Math.round((status.scanned / status.total) * 100) : 0

    const stepLabel: Record<NonNullable<OracleTgMediaPrepareProgress['current_step']>, string> = {
        connecting: 'Connecting to Telegram',
        resolving: 'Resolving channel',
        fetching: 'Fetching message',
        downloading: 'Downloading media',
        transcribing: 'Transcribing with Gemini',
        writing: 'Saving transcript',
        done: 'Done',
    }

    const fmtBytes = (n: number | null): string => {
        if (!n) return ''
        if (n < 1024) return `${n} B`
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
        return `${(n / 1024 / 1024).toFixed(1)} MB`
    }

    const stepElapsed = (() => {
        if (!status?.current_started_at) return ''
        const ms = Date.now() - new Date(status.current_started_at).getTime()
        if (ms < 1000) return ''
        const s = Math.floor(ms / 1000)
        if (s < 60) return `${s}s`
        return `${Math.floor(s / 60)}m ${s % 60}s`
    })()

    return (
        <>
            <Button
                size="sm"
                variant="outlined"
                color={pendingCount > 0 ? 'warning' : 'neutral'}
                startDecorator={<MicIcon sx={{ fontSize: 14 }} />}
                onClick={() => setOpen(true)}
                disabled={pendingCount === 0 && !isRunning}
            >
                Prepare media{pendingCount > 0 ? ` · ${pendingCount}` : ''}
            </Button>
            {open && (
                <Modal open onClose={() => setOpen(false)}>
                    <ModalDialog sx={{ minWidth: 440 }}>
                        <ModalClose />
                        <Typography level="h4" startDecorator={<MicIcon />}>
                            Prepare media
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                            Re-fetches every message whose media never got transcribed and runs it through Gemini. Safe to run repeatedly — already-transcribed
                            rows are skipped.
                        </Typography>

                        {error && (
                            <Alert color="danger" size="sm">
                                {error}
                            </Alert>
                        )}

                        {status && status.status !== 'idle' && (
                            <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                                <Stack gap={1}>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <Chip
                                            size="sm"
                                            color={
                                                status.status === 'running'
                                                    ? 'primary'
                                                    : status.status === 'completed'
                                                      ? 'success'
                                                      : status.status === 'cancelled'
                                                        ? 'neutral'
                                                        : 'danger'
                                            }
                                            variant="soft"
                                        >
                                            {status.status}
                                        </Chip>
                                        <Typography level="body-sm">
                                            {status.scanned} / {status.total} scanned
                                        </Typography>
                                    </Stack>
                                    {isRunning && <LinearProgress determinate value={progressPct} />}
                                    {isRunning && status.current_step && status.current_step !== 'done' && (
                                        <Stack gap={0.5} sx={{ pl: 0.5, borderLeft: '2px solid', borderColor: 'primary.300' }}>
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                <CircularProgress size="sm" thickness={2} />
                                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                                    {stepLabel[status.current_step]}
                                                </Typography>
                                                {stepElapsed && (
                                                    <Typography level="body-xs" color="neutral">
                                                        · {stepElapsed}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <Typography level="body-xs" color="neutral">
                                                {status.current_media_type ? `${status.current_media_type} ` : ''}
                                                {status.current_chat_id ? `· chat ${status.current_chat_id} ` : ''}
                                                {status.current_message_id ? `· msg ${status.current_message_id}` : ''}
                                                {status.current_bytes ? ` · ${fmtBytes(status.current_bytes)}` : ''}
                                            </Typography>
                                            {status.current_step === 'transcribing' && (
                                                <Typography level="body-xs" color="warning">
                                                    Gemini can take 30s–3min on long voice notes. The UI keeps polling.
                                                </Typography>
                                            )}
                                        </Stack>
                                    )}
                                    <Stack direction="row" gap={2}>
                                        <Typography level="body-xs">Transcribed: {status.transcribed}</Typography>
                                        <Typography level="body-xs">Failed: {status.failed}</Typography>
                                        <Typography level="body-xs">Skipped: {status.skipped}</Typography>
                                    </Stack>
                                    {status.error && (
                                        <Typography level="body-xs" color="danger">
                                            {status.error}
                                        </Typography>
                                    )}
                                </Stack>
                            </Sheet>
                        )}

                        <Stack direction="row" gap={1} justifyContent="flex-end">
                            {isRunning ? (
                                <Button size="sm" variant="outlined" color="danger" startDecorator={<StopIcon />} onClick={cancel}>
                                    Cancel
                                </Button>
                            ) : (
                                <Button
                                    size="sm"
                                    variant="solid"
                                    color="primary"
                                    startDecorator={<PlayArrowIcon />}
                                    loading={starting}
                                    disabled={pendingCount === 0}
                                    onClick={start}
                                >
                                    Start ({pendingCount})
                                </Button>
                            )}
                        </Stack>
                    </ModalDialog>
                </Modal>
            )}
        </>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Accounts table
// ─────────────────────────────────────────────────────────────────────────────

function AccountsTable({
    accounts,
    loading,
    onManage,
    onStart,
    onStop,
    onLogout,
}: {
    accounts: OracleTgAccount[]
    loading: boolean
    onManage: (a: OracleTgAccount) => void
    onStart: (phone: string) => Promise<void>
    onStop: (phone: string) => Promise<void>
    onLogout: (phone: string) => Promise<void>
}): JSX.Element {
    if (loading) {
        return (
            <Stack alignItems="center" py={4}>
                <CircularProgress size="sm" />
            </Stack>
        )
    }
    if (accounts.length === 0) {
        return (
            <Sheet variant="soft" sx={{ p: 3, borderRadius: 'sm', textAlign: 'center' }}>
                <Typography level="body-sm" color="neutral">
                    No accounts connected yet. Click <strong>Add Account</strong> to sign in with a Telegram user account.
                </Typography>
            </Sheet>
        )
    }
    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 }}>
                <thead>
                    <tr>
                        <Th>Phone</Th>
                        <Th>Status</Th>
                        <Th>Created</Th>
                        <Th>Actions</Th>
                    </tr>
                </thead>
                <tbody>
                    {accounts.map((a) => (
                        <tr key={a.phone}>
                            <Td>
                                <Stack direction="row" alignItems="center" gap={0.75}>
                                    <PhoneIphoneIcon sx={{ fontSize: 16, color: 'text.tertiary' }} />
                                    {a.phone}
                                </Stack>
                            </Td>
                            <Td>
                                <Chip
                                    size="sm"
                                    variant="soft"
                                    color={a.status === 'active' && a.running ? 'success' : a.status === 'logged_out' ? 'danger' : 'warning'}
                                >
                                    {a.status === 'active' && a.running ? 'running' : a.status}
                                </Chip>
                            </Td>
                            <Td>{new Date(a.createdAt).toLocaleString()}</Td>
                            <Td>
                                <Stack direction="row" gap={0.5}>
                                    <Button size="sm" variant="outlined" startDecorator={<GroupIcon sx={{ fontSize: 14 }} />} onClick={() => onManage(a)}>
                                        Manage
                                    </Button>
                                    {a.running ? (
                                        <Button
                                            size="sm"
                                            variant="soft"
                                            color="danger"
                                            startDecorator={<StopIcon sx={{ fontSize: 14 }} />}
                                            onClick={() => void onStop(a.phone)}
                                        >
                                            Stop
                                        </Button>
                                    ) : (
                                        <Button
                                            size="sm"
                                            variant="soft"
                                            color="success"
                                            startDecorator={<PlayArrowIcon sx={{ fontSize: 14 }} />}
                                            onClick={() => void onStart(a.phone)}
                                            disabled={a.status === 'logged_out'}
                                        >
                                            Start
                                        </Button>
                                    )}
                                    <Tooltip title="Log out (clears session)" size="sm">
                                        <IconButton size="sm" variant="soft" color="danger" onClick={() => void onLogout(a.phone)}>
                                            <LogoutIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </Box>
        </Box>
    )
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
    return (
        <Box
            component="th"
            sx={{
                textAlign: 'left',
                p: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                fontWeight: 'lg',
                color: 'text.tertiary',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
            }}
        >
            {children}
        </Box>
    )
}

function Td({ children }: { children: React.ReactNode }): JSX.Element {
    return (
        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            {children}
        </Box>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Sign-in modal
// ─────────────────────────────────────────────────────────────────────────────

function SignInModal({ open, onClose, onSuccess }: { open: boolean; onClose: () => void; onSuccess: () => Promise<void> }): JSX.Element {
    const [step, setStep] = useState<1 | 2>(1)
    const [phone, setPhone] = useState('')
    const [code, setCode] = useState('')
    const [password, setPassword] = useState('')
    const [showCode, setShowCode] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')

    const reset = () => {
        setStep(1)
        setPhone('')
        setCode('')
        setPassword('')
        setShowCode(false)
        setShowPassword(false)
        setBusy(false)
        setError('')
    }

    const handleSend = async () => {
        setBusy(true)
        setError('')
        try {
            await oracleTgSendCode(phone.trim())
            setStep(2)
        } catch (err) {
            setError((err as Error)?.message || String(err))
        } finally {
            setBusy(false)
        }
    }
    const handleSignIn = async () => {
        setBusy(true)
        setError('')
        try {
            await oracleTgSignIn(phone.trim(), code.trim(), password || undefined)
            reset()
            await onSuccess()
        } catch (err) {
            setError((err as Error)?.message || String(err))
        } finally {
            setBusy(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={() => {
                reset()
                onClose()
            }}
        >
            <ModalDialog sx={{ minWidth: 480 }}>
                <ModalClose />
                <Typography level="h4">Sign in to Telegram</Typography>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                    {step === 1 ? "We'll send a login code to your Telegram app." : `Code sent to ${phone}`}
                </Typography>
                <Divider sx={{ my: 1 }} />
                {busy && <LinearProgress sx={{ mb: 1 }} />}
                {error && (
                    <Alert color="danger" size="sm" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}

                {step === 1 && (
                    <Stack spacing={1.5}>
                        <FormControl>
                            <FormLabel>Phone number</FormLabel>
                            <Input
                                startDecorator={<PhoneIphoneIcon />}
                                placeholder="+1 555 123 4567"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                            />
                        </FormControl>
                        <Button onClick={() => void handleSend()} disabled={phone.trim().length < 6 || busy} loading={busy} fullWidth>
                            Send code
                        </Button>
                    </Stack>
                )}
                {step === 2 && (
                    <Stack spacing={1.5}>
                        <FormControl>
                            <FormLabel>Code from Telegram</FormLabel>
                            <Input
                                startDecorator={<KeyIcon />}
                                type={showCode ? 'text' : 'password'}
                                placeholder="12345"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                endDecorator={
                                    <IconButton size="sm" variant="plain" onClick={() => setShowCode((v) => !v)}>
                                        {showCode ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                }
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Password (2FA, if enabled)</FormLabel>
                            <Input
                                startDecorator={<LockIcon />}
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Cloud password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                endDecorator={
                                    <IconButton size="sm" variant="plain" onClick={() => setShowPassword((v) => !v)}>
                                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                }
                            />
                        </FormControl>
                        <Stack direction="row" spacing={1}>
                            <Button variant="soft" onClick={() => setStep(1)}>
                                Back
                            </Button>
                            <Box sx={{ flex: 1 }} />
                            <Button onClick={() => void handleSignIn()} disabled={!code.trim() || busy} loading={busy} color="success">
                                Sign in
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </ModalDialog>
        </Modal>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Manage Watched Groups modal
// ─────────────────────────────────────────────────────────────────────────────

export function ManageWatchedGroupsModal({
    account,
    onClose,
    mode = 'dedicated',
}: {
    account: OracleTgAccount
    onClose: () => void
    mode?: 'dedicated' | 'customer'
}): JSX.Element {
    const [dialogs, setDialogs] = useState<ResolvedPeer[]>([])
    const [watched, setWatched] = useState<OracleTgGroup[]>([])
    const [loading, setLoading] = useState(true)
    const [joinInput, setJoinInput] = useState('')
    const [search, setSearch] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [editingBrokerFor, setEditingBrokerFor] = useState<string | null>(null)
    const [brokerDraft, setBrokerDraft] = useState('')
    const [backfillFor, setBackfillFor] = useState<OracleTgGroup | null>(null)

    const refresh = useCallback(async () => {
        setLoading(true)
        try {
            const listGroups = mode === 'customer' ? oracleTgListCustomerGroupsForAccount : oracleTgListGroupsForAccount
            const [d, w] = await Promise.all([oracleTgListDialogs(account.phone, 80).catch(() => []), listGroups(account.phone).catch(() => [])])
            setDialogs(d)
            setWatched(w)
        } finally {
            setLoading(false)
        }
    }, [account.phone, mode])

    useEffect(() => {
        void refresh()
    }, [refresh])

    const watchedSet = useMemo(() => new Set(watched.map((g) => g.peerId)), [watched])

    const handleJoin = async () => {
        const input = joinInput.trim()
        if (!input) return
        setBusy(true)
        setError('')
        try {
            await oracleTgJoinChat(account.phone, input)
            setJoinInput('')
            await refresh()
        } catch (err) {
            setError((err as Error)?.message || String(err))
        } finally {
            setBusy(false)
        }
    }
    const handleToggle = async (peer: ResolvedPeer | OracleTgGroup, watch: boolean) => {
        const peerId = peer.peerId
        const title = ('title' in peer ? peer.title : null) ?? ('originalName' in peer ? peer.originalName : null) ?? undefined
        try {
            if (watch) {
                if (mode === 'customer') {
                    await oracleTgWatchCustomerGroup(account.phone, peerId, { title })
                } else {
                    await oracleTgWatchGroup(account.phone, peerId, { title })
                }
                await refresh()
                if (mode === 'customer') {
                    setEditingBrokerFor(peerId)
                    setBrokerDraft('')
                }
            } else {
                if (mode === 'customer') {
                    await oracleTgUnwatchCustomerGroup(account.phone, peerId)
                } else {
                    await oracleTgUnwatchGroup(account.phone, peerId)
                }
                await refresh()
            }
        } catch (err) {
            setError((err as Error)?.message || String(err))
        }
    }
    const saveBroker = async (peerId: string) => {
        try {
            if (mode === 'customer') {
                await oracleTgUpdateCustomerGroup(peerId, { brokerCompanyName: brokerDraft.trim() || null })
            } else {
                await oracleTgUpdateGroup(peerId, { brokerCompanyName: brokerDraft.trim() || null })
            }
            setEditingBrokerFor(null)
            setBrokerDraft('')
            await refresh()
        } catch (err) {
            setError((err as Error)?.message || String(err))
        }
    }

    const filterFn = (title: string | null | undefined, peerId: string) => {
        if (!search.trim()) return true
        const s = search.toLowerCase()
        return (title ?? '').toLowerCase().includes(s) || peerId.toLowerCase().includes(s)
    }

    const availableUnwatched = dialogs.filter((d) => !watchedSet.has(d.peerId)).filter((d) => filterFn(d.title, d.peerId))
    // dedicated mode: only untagged groups (no brokerCompanyName) — they feed the dedicated DataSource
    // customer mode: all watched groups — tagged ones feed the customer DataSource, untagged ones are
    //   visible so the user can add a broker tag (they disappear after tagging when viewing dedicated mode)
    const watchedFiltered = watched.filter((g) => mode === 'customer' || !g.brokerCompanyName).filter((g) => filterFn(g.originalName, g.peerId))

    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{ minWidth: 720, maxWidth: 880, maxHeight: '90vh', overflow: 'auto' }}>
                <ModalClose />
                <Typography level="h4" sx={{ mb: 0.5 }}>
                    Manage Watched Groups
                </Typography>
                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <Typography level="body-sm" color="neutral">
                        Account:
                    </Typography>
                    <Chip size="sm" variant="soft">
                        {account.phone}
                    </Chip>
                </Stack>
                <Divider sx={{ my: 1 }} />
                {loading && <LinearProgress sx={{ mb: 1 }} />}
                {error && (
                    <Alert color="danger" size="sm" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}

                <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', mb: 1.5 }}>
                    <Typography level="body-sm" fontWeight="lg" sx={{ mb: 0.5 }}>
                        Join New Group/Channel
                    </Typography>
                    <Stack direction="row" gap={1}>
                        <Input placeholder="Paste invite link or @username" value={joinInput} onChange={(e) => setJoinInput(e.target.value)} sx={{ flex: 1 }} />
                        <Button onClick={() => void handleJoin()} disabled={!joinInput.trim() || busy} loading={busy}>
                            Join &amp; Watch
                        </Button>
                    </Stack>
                </Sheet>

                <Input
                    startDecorator={<SearchIcon />}
                    placeholder="Search groups and channels..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 1.5 }}
                />

                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <Typography level="body-sm" fontWeight="lg" color="success">
                        Watching ({watchedFiltered.length})
                    </Typography>
                </Stack>
                <Stack gap={0.5} sx={{ mb: 2 }}>
                    {watchedFiltered.length === 0 && (
                        <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic', px: 1 }}>
                            None
                        </Typography>
                    )}
                    {watchedFiltered.map((g) => (
                        <Sheet key={g.peerId} variant="soft" color="success" sx={{ borderRadius: 'sm', p: 1 }}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Stack flex={1} minWidth={0}>
                                    <Typography level="body-sm" fontWeight="lg">
                                        {g.originalName || g.peerId}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" gap={0.5}>
                                        <Typography level="body-xs" color="neutral">
                                            Broker:
                                        </Typography>
                                        {editingBrokerFor === g.peerId ? (
                                            <>
                                                <Input
                                                    size="sm"
                                                    value={brokerDraft}
                                                    onChange={(e) => setBrokerDraft(e.target.value)}
                                                    placeholder="e.g. JB Hunt"
                                                    sx={{ width: 180 }}
                                                />
                                                <Button size="sm" variant="solid" onClick={() => void saveBroker(g.peerId)}>
                                                    Save
                                                </Button>
                                                <Button size="sm" variant="plain" onClick={() => setEditingBrokerFor(null)}>
                                                    Cancel
                                                </Button>
                                            </>
                                        ) : (
                                            <Chip
                                                size="sm"
                                                variant={!g.brokerCompanyName && mode === 'customer' ? 'solid' : 'soft'}
                                                color={g.brokerCompanyName ? 'warning' : mode === 'customer' ? 'danger' : 'neutral'}
                                                onClick={() => {
                                                    setEditingBrokerFor(g.peerId)
                                                    setBrokerDraft(g.brokerCompanyName ?? '')
                                                }}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {g.brokerCompanyName || (mode === 'customer' ? 'set broker tag' : 'add tag')}
                                            </Chip>
                                        )}
                                    </Stack>
                                </Stack>
                                {mode !== 'customer' && (
                                    <Tooltip title="Backfill history" size="sm">
                                        <IconButton size="sm" variant="outlined" onClick={() => setBackfillFor(g)}>
                                            <SyncIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Switch checked onChange={() => void handleToggle(g, false)} />
                            </Stack>
                        </Sheet>
                    ))}
                </Stack>

                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <Typography level="body-sm" fontWeight="lg">
                        Available Groups ({availableUnwatched.length})
                    </Typography>
                </Stack>
                <Stack gap={0.5}>
                    {availableUnwatched.length === 0 && (
                        <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic', px: 1 }}>
                            All visible dialogs are already watched.
                        </Typography>
                    )}
                    {availableUnwatched.map((d) => (
                        <Sheet key={d.peerId} variant="outlined" sx={{ borderRadius: 'sm', p: 1 }}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Stack flex={1} minWidth={0}>
                                    <Typography level="body-sm">{d.title || d.peerId}</Typography>
                                    <Stack direction="row" gap={0.5}>
                                        <Chip size="sm" variant="soft" color="neutral">
                                            {d.isChannel ? 'Channel' : d.isGroup ? 'Group' : 'Chat'}
                                        </Chip>
                                        {d.username && (
                                            <Chip size="sm" variant="soft" color="primary">
                                                @{d.username}
                                            </Chip>
                                        )}
                                    </Stack>
                                </Stack>
                                <Switch checked={false} onChange={() => void handleToggle(d, true)} />
                            </Stack>
                        </Sheet>
                    ))}
                </Stack>
                {backfillFor && <BackfillDialog phone={account.phone} group={backfillFor} onClose={() => setBackfillFor(null)} />}
            </ModalDialog>
        </Modal>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Backfill dialog
// ─────────────────────────────────────────────────────────────────────────────

export function BackfillDialog({
    phone,
    group,
    onClose,
    onComplete,
    getBackfillStatus = oracleTgGetBackfillStatus,
    startBackfillFn = oracleTgStartBackfill,
    cancelBackfillFn = oracleTgCancelBackfill,
}: {
    phone: string
    group: OracleTgGroup
    onClose: () => void
    onComplete?: () => void
    getBackfillStatus?: (phone: string, peerId: string) => Promise<OracleTgBackfillProgress>
    startBackfillFn?: (opts: { phone: string; peerId: string; limit?: number; since?: string; transcribeMedia?: boolean }) => Promise<void>
    cancelBackfillFn?: (phone: string, peerId: string) => Promise<void>
}): JSX.Element {
    const [progress, setProgress] = useState<OracleTgBackfillProgress | null>(null)
    const [limit, setLimit] = useState(500)
    const [since, setSince] = useState('')
    const [transcribeMedia, setTranscribeMedia] = useState(true)
    const [error, setError] = useState('')

    const refresh = useCallback(async () => {
        try {
            const p = await getBackfillStatus(phone, group.peerId)
            setProgress(p)
        } catch (err) {
            setError((err as Error)?.message || String(err))
        }
    }, [phone, group.peerId, getBackfillStatus])

    useEffect(() => {
        void refresh()
    }, [refresh])

    useEffect(() => {
        if (progress?.status !== 'running') return
        const interval = setInterval(() => void refresh(), 1500)
        return () => clearInterval(interval)
    }, [progress?.status, refresh])

    useEffect(() => {
        if (progress?.status === 'completed') onComplete?.()
    }, [progress?.status, onComplete])

    const start = async () => {
        setError('')
        try {
            await startBackfillFn({ phone, peerId: group.peerId, limit, since: since || undefined, transcribeMedia })
            await refresh()
        } catch (err) {
            setError((err as Error)?.message || String(err))
        }
    }
    const cancel = async () => {
        await cancelBackfillFn(phone, group.peerId)
        await refresh()
    }

    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{ minWidth: 520 }}>
                <ModalClose />
                <Typography level="h4">Backfill</Typography>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                    {group.originalName || group.peerId} <code>({group.peerId})</code>
                </Typography>
                <Divider sx={{ my: 1 }} />
                {error && (
                    <Alert color="danger" size="sm" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}
                {progress?.status === 'running' ? (
                    <Stack gap={1}>
                        <Alert color="primary" size="sm">
                            Running — processed <strong>{progress.processed}</strong>, ingested <strong>{progress.ingested}</strong>, duplicates{' '}
                            {progress.duplicates}, media transcribed {progress.media_transcribed} (failed {progress.media_failed}).
                        </Alert>
                        {progress.last_message_at && (
                            <Typography level="body-xs" color="neutral">
                                Walked back to {new Date(progress.last_message_at).toLocaleString()}
                            </Typography>
                        )}
                        <Button color="danger" variant="soft" onClick={() => void cancel()}>
                            Cancel backfill
                        </Button>
                    </Stack>
                ) : (
                    <Stack gap={1}>
                        {progress && progress.status !== 'idle' && (
                            <Alert size="sm" color={progress.status === 'completed' ? 'success' : progress.status === 'cancelled' ? 'neutral' : 'danger'}>
                                Last run: {progress.status} — processed {progress.processed}, ingested {progress.ingested}, media transcribed{' '}
                                {progress.media_transcribed}
                                {progress.error ? ` (${progress.error})` : ''}
                            </Alert>
                        )}
                        <Stack direction="row" gap={1} alignItems="center">
                            <Typography level="body-xs" sx={{ minWidth: 110, color: 'text.tertiary' }}>
                                Max messages
                            </Typography>
                            <Input
                                size="sm"
                                type="number"
                                value={limit}
                                onChange={(e) => setLimit(Math.max(1, Math.min(5000, Number(e.target.value) || 0)))}
                                sx={{ width: 120 }}
                            />
                        </Stack>
                        <Stack direction="row" gap={1} alignItems="center">
                            <Typography level="body-xs" sx={{ minWidth: 110, color: 'text.tertiary' }}>
                                Since (optional)
                            </Typography>
                            <Input size="sm" type="date" value={since} onChange={(e) => setSince(e.target.value)} sx={{ width: 180 }} />
                        </Stack>
                        <Stack direction="row" gap={1} alignItems="center">
                            <Typography level="body-xs" sx={{ minWidth: 110, color: 'text.tertiary' }}>
                                Transcribe media
                            </Typography>
                            <Switch checked={transcribeMedia} onChange={(e) => setTranscribeMedia(e.target.checked)} />
                        </Stack>
                        <Button onClick={() => void start()} color="primary">
                            Start backfill
                        </Button>
                    </Stack>
                )}
            </ModalDialog>
        </Modal>
    )
}

function WeeklyBars({ data }: { data: { date: string; count: number }[] }): JSX.Element {
    const max = Math.max(1, ...data.map((d) => d.count))
    return (
        <Stack direction="row" alignItems="flex-end" gap={0.5} sx={{ height: 60 }}>
            {data.map((d) => {
                const h = Math.max(4, Math.round((d.count / max) * 56))
                return (
                    <Tooltip key={d.date} title={`${d.date}: ${d.count}`} size="sm">
                        <Box sx={{ flex: 1, height: h, bgcolor: 'primary.300', borderRadius: 2 }} />
                    </Tooltip>
                )
            })}
        </Stack>
    )
}

function relTime(iso: string | null | undefined): string {
    if (!iso) return 'never'
    const diffMs = Date.now() - new Date(iso).getTime()
    const s = Math.floor(diffMs / 1000)
    if (s < 60) return `${s}s ago`
    const m = Math.floor(s / 60)
    if (m < 60) return `${m}m ago`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h ago`
    const d = Math.floor(h / 24)
    return `${d}d ago`
}

function statusColor(s: 'pending' | 'processed' | 'unattributed' | 'failed'): 'warning' | 'success' | 'neutral' | 'danger' {
    return s === 'pending' ? 'warning' : s === 'processed' ? 'success' : s === 'failed' ? 'danger' : 'neutral'
}

function MessageDetailModal({ message, onClose }: { message: TelegramDedicatedRecentMessage; onClose: () => void }): JSX.Element {
    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{ minWidth: 600, maxWidth: 800, maxHeight: '85vh', overflow: 'auto' }}>
                <ModalClose />
                <Typography level="h4">Message details</Typography>
                <Divider sx={{ my: 1 }} />
                <Stack gap={1}>
                    <DetailRow label="From">{message.from_username || message.from_id}</DetailRow>
                    <DetailRow label="Created">{new Date(message.created_at).toLocaleString()}</DetailRow>
                    <DetailRow label="Status">{message.status}</DetailRow>
                    {message.processed_at && <DetailRow label="Processed">{new Date(message.processed_at).toLocaleString()}</DetailRow>}
                    {message.forward_from && <DetailRow label="Forwarded from">{message.forward_from}</DetailRow>}
                    {message.reply_to_id && <DetailRow label="Reply to">{message.reply_to_id}</DetailRow>}
                    {message.text && (
                        <DetailRow label="Text">
                            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, p: 1, bgcolor: 'background.level1', borderRadius: 'xs' }}>
                                {message.text}
                            </Box>
                        </DetailRow>
                    )}
                    {message.caption && <DetailRow label="Caption">{message.caption}</DetailRow>}
                    {message.media_type && (
                        <DetailRow label="Media">
                            {message.media_type} · {message.media_status ?? '—'}
                            {message.file_name && <Typography level="body-xs">file: {message.file_name}</Typography>}
                            {message.audio_title && (
                                <Typography level="body-xs">
                                    audio: {message.audio_performer} — {message.audio_title}
                                </Typography>
                            )}
                        </DetailRow>
                    )}
                    {message.transcribed_text && (
                        <DetailRow label="Transcript">
                            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, p: 1, bgcolor: 'background.level1', borderRadius: 'xs' }}>
                                {message.transcribed_text}
                            </Box>
                        </DetailRow>
                    )}
                    {message.extracted_entities && (
                        <DetailRow label="Entities">
                            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 11, p: 1, bgcolor: 'background.level1', borderRadius: 'xs' }}>
                                {JSON.stringify(message.extracted_entities, null, 2)}
                            </Box>
                        </DetailRow>
                    )}
                </Stack>
            </ModalDialog>
        </Modal>
    )
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
    return (
        <Stack direction="row" gap={1} alignItems="flex-start">
            <Typography level="body-xs" color="neutral" sx={{ minWidth: 100, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.04em', pt: 0.25 }}>
                {label}
            </Typography>
            <Box flex={1} minWidth={0}>
                {typeof children === 'string' ? <Typography level="body-sm">{children}</Typography> : children}
            </Box>
        </Stack>
    )
}
